import { Injectable, inject } from '@angular/core';
import { BizzdesignSdk } from '@bizzdesign/bizzdesign-sdk';
import { type AuthenticatedUser } from '@bizzdesign/sdk-auth';
import {
  InvitationAnalyticsService,
  type InvitationContext,
  type InvitationSource,
  ProductAnalyticsService,
} from '@bizzdesign/shared/services';

type FirstLoginData = {
  readonly firstLoginAt: string | undefined;
  readonly hasElevatedRole: boolean;
  readonly loginMethod: LoginMethod;
};

type LoginMethod = 'email_password' | 'sso';

const DEFAULT_FIRST_LOGIN_THRESHOLD_SECONDS = 300;
const ONBOARDING_WORKSPACE_CREATED_KEY_PREFIX =
  'bd.onboarding.workspaceCreated';

@Injectable({ providedIn: 'root' })
export class OnboardingFirstLoginService {
  private readonly sdk = inject(BizzdesignSdk);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  private readonly invitationAnalyticsService = inject(
    InvitationAnalyticsService,
  );
  private firstLoginDataPromise:
    | Promise<FirstLoginData | undefined>
    | undefined;
  private firstLoginTracked = false;
  private getStartedDismissed = false;
  private userId: string | undefined;

  dismissGetStarted(): void {
    this.getStartedDismissed = true;
  }

  isRecentFirstLogin(
    thresholdSeconds = DEFAULT_FIRST_LOGIN_THRESHOLD_SECONDS,
  ): Promise<boolean> {
    return this.evaluateFirstLogin(thresholdSeconds).then(
      ({ isRecent }) => isRecent && !this.getStartedDismissed,
    );
  }

  isRecentFirstLoginWithElevatedRole(): Promise<boolean> {
    return this.evaluateFirstLogin().then(
      ({ isRecent, hasElevatedRole }) =>
        isRecent && hasElevatedRole && !this.hasCreatedOnboardingWorkspace(),
    );
  }

  markOnboardingWorkspaceCreated(): void {
    try {
      localStorage.setItem(this.onboardingWorkspaceCreatedKey(), 'true');
    } catch {
      // Non-critical — silently fail if localStorage is unavailable
    }
  }

  private hasCreatedOnboardingWorkspace(): boolean {
    try {
      return (
        localStorage.getItem(this.onboardingWorkspaceCreatedKey()) === 'true'
      );
    } catch {
      return false;
    }
  }

  private onboardingWorkspaceCreatedKey(): string {
    return this.userId
      ? `${ONBOARDING_WORKSPACE_CREATED_KEY_PREFIX}:${this.userId}`
      : ONBOARDING_WORKSPACE_CREATED_KEY_PREFIX;
  }

  private evaluateFirstLogin(
    thresholdSeconds = DEFAULT_FIRST_LOGIN_THRESHOLD_SECONDS,
  ): Promise<{ isRecent: boolean; hasElevatedRole: boolean }> {
    this.firstLoginDataPromise ??= this.loadFirstLoginData();
    return this.firstLoginDataPromise.then((data) => {
      if (!data) return { isRecent: false, hasElevatedRole: false };

      const isRecent = this.isWithinFirstLoginThreshold(
        data.firstLoginAt,
        Date.now(),
        thresholdSeconds * 1_000,
      );

      if (isRecent && !this.firstLoginTracked) {
        this.firstLoginTracked = true;
        this.trackFirstLogin(data);
      }

      return { isRecent, hasElevatedRole: data.hasElevatedRole };
    });
  }

  private async loadFirstLoginData(): Promise<FirstLoginData | undefined> {
    try {
      const authenticatedUser =
        await this.sdk.authClient.getAuthenticatedUser();
      this.userId = authenticatedUser.userId;

      const { value: user } = await this.sdk.searchClient
        .searchUsers({
          ids: [authenticatedUser.userId],
          limit: 1,
        })
        .next();

      return {
        firstLoginAt: user?.firstLoginAt,
        hasElevatedRole: !this.hasOnlyBaseRole(authenticatedUser),
        loginMethod: await this.resolveLoginMethod(),
      };
    } catch {
      this.firstLoginDataPromise = undefined;
    }

    return undefined;
  }

  private trackFirstLogin(data: FirstLoginData): void {
    this.productAnalyticsService.sendEvent('first_login', {
      login_method: data.loginMethod,
      ...this.getFirstLoginInvitationProperties(data.loginMethod),
    });
  }

  private getFirstLoginInvitationProperties(loginMethod: LoginMethod): {
    readonly invitation_context: InvitationContext;
    readonly invitation_source: InvitationSource;
  } {
    const invitationData =
      this.invitationAnalyticsService.getStoredInvitationData();

    if (invitationData !== null) {
      return this.invitationAnalyticsService.getInvitationProperties(
        invitationData,
      );
    }

    return {
      invitation_context: 'production',
      invitation_source: loginMethod === 'sso' ? 'sso' : 'user_management',
    };
  }

  private async resolveLoginMethod(): Promise<LoginMethod> {
    try {
      const idToken = await this.sdk.authTokenProvider.getIdToken();
      const payload = this.decodeJwtPayload(idToken);
      return this.hasSsoIdentities(payload) ? 'sso' : 'email_password';
    } catch {
      return 'email_password';
    }
  }

  private decodeJwtPayload(idToken: string): Record<string, unknown> {
    const payloadPart = idToken.split('.')[1];
    if (!payloadPart) return {};
    const decoded = this.decodeBase64Url(payloadPart);
    const payload: unknown = JSON.parse(decoded);
    return this.isRecord(payload) ? payload : {};
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    return atob(padded);
  }

  private hasSsoIdentities(payload: Record<string, unknown>): boolean {
    const identities = payload['identities'];
    if (Array.isArray(identities)) return identities.length > 0;
    if (typeof identities !== 'string' || identities.trim().length === 0) {
      return false;
    }
    try {
      const parsed: unknown = JSON.parse(identities);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private hasOnlyBaseRole(authenticatedUser: AuthenticatedUser): boolean {
    const roles = authenticatedUser.roles;
    const hasBaseRole = roles.base === true || roles.isBase === true;
    const hasElevatedRole =
      roles.admin === true ||
      roles.isAdmin === true ||
      roles.pro === true ||
      roles.isPro === true ||
      roles.core === true ||
      roles.isCore === true ||
      roles.sys_admin === true ||
      roles.sysadmin === true ||
      roles.isSysAdmin === true;

    return hasBaseRole && !hasElevatedRole;
  }

  private isWithinFirstLoginThreshold(
    firstLoginAt: string | undefined,
    now: number,
    thresholdMs: number,
  ): boolean {
    if (!firstLoginAt) {
      return false;
    }

    const firstLoginAtTimestamp = Date.parse(firstLoginAt);

    if (Number.isNaN(firstLoginAtTimestamp)) {
      return false;
    }

    const elapsedTimeSinceFirstLoginMs = now - firstLoginAtTimestamp;

    return (
      elapsedTimeSinceFirstLoginMs >= 0 &&
      elapsedTimeSinceFirstLoginMs <= thresholdMs
    );
  }
}
